import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Lan as PortIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorOutlineIcon,
  Circle as CircleIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../services/useApi';
import OnusModal from './OnusModal';
import CtosModal from './CtosModal';
import OlTCardPortForm from '../../../components/forms/OlTCardPortForm';

interface Port {
  id: number;
  portNumber: string;
  slot: number;
  status: 'active' | 'inactive' | 'maintenance';
  onuCount: number;
  ctoCount?: number;
  ctoAlert?: 'none' | 'healthy' | 'partial_offline' | 'all_offline';
  maxOnus: number;
  txpower?: number;
  description?: string;
  lastUpdate?: string;
}

interface Card {
  id: number;
  slot: number;
  model?: string;
  qtde_portas: number;
  serialnumber?: string;
  sw?: string;
  status?: string;
  description?: string;
  last_sync?: string;
  olt?: {
    id: number;
    name: string;
    vendor: string;
  };
}

interface PortsModalProps {
  open: boolean;
  onClose: () => void;
  selectedCard: Card | null;
  oltId?: string;
}

const PortsModal: React.FC<PortsModalProps> = ({
  open,
  onClose,
  selectedCard,
  oltId
}) => {
  const [ports, setPorts] = useState<Port[]>([]);
  const [portsLoading, setPortsLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [onusModalOpen, setOnusModalOpen] = useState(false);
  const [ctosModalOpen, setCtosModalOpen] = useState(false);
  const [selectedPortForOnus, setSelectedPortForOnus] = useState<Port | null>(null);
  const [selectedPortForCtos, setSelectedPortForCtos] = useState<Port | null>(null);
  const [portFormOpen, setPortFormOpen] = useState(false);
  const [portToEdit, setPortToEdit] = useState<Port | null>(null);
  const [removePortId, setRemovePortId] = useState<number | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Função para buscar portas do card
  const fetchPorts = async (slot: number) => {
    if (!oltId) return;

    setPortsLoading(true);
    try {
      console.log(`🔍 Buscando portas para OLT ${oltId}, slot ${slot}`);
      const response = await api.get(`/olts/${oltId}/cards/${slot}/ports`);
      console.log('📡 Resposta da API:', response);

      if (response.data.success) {
        setPorts(sortPortsByNumber(response.data.data));
      } else {
        toast.error('Erro ao carregar portas');
        setPorts([]);
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar portas:', err);
      console.error('❌ Detalhes do erro:', err.response?.data);
      toast.error('Erro ao carregar portas');
      setPorts([]);
    } finally {
      setPortsLoading(false);
    }
  };

  // Função para sincronizar portas do card
  const syncPorts = async () => {
    if (!oltId || !selectedCard) return;

    setSyncLoading(true);
    try {
      console.log(`🔄 Sincronizando portas para OLT ${oltId}, slot ${selectedCard.slot}`);
      const response = await api.post(`/olts/${oltId}/cards/${selectedCard.slot}/ports/sync`);
      console.log('📡 Resposta da sincronização:', response);

      if (response.data.success) {
        const { portsCreated, portsUpdated, portsRemoved } = response.data.data;
        const parts = [];
        if (portsCreated > 0) parts.push(`${portsCreated} criadas`);
        if (portsUpdated > 0) parts.push(`${portsUpdated} atualizadas`);
        if (portsRemoved > 0) parts.push(`${portsRemoved} removidas`);
        toast.success(`Sincronização concluída${parts.length ? `: ${parts.join(', ')}` : ''}.`);

        // Recarregar portas após sincronização
        await fetchPorts(selectedCard.slot);
      } else {
        toast.error('Erro ao sincronizar portas');
      }
    } catch (err: any) {
      console.error('❌ Erro ao sincronizar portas:', err);
      console.error('❌ Detalhes do erro:', err.response?.data);
      toast.error('Erro ao sincronizar portas');
    } finally {
      setSyncLoading(false);
    }
  };

  // Função para iniciar edição
  const handleStartEdit = (port: Port) => {
    setPortToEdit(port);
    setPortFormOpen(true);
  };

  const handleOpenOnusModal = (port: Port) => {
    setSelectedPortForOnus(port);
    setOnusModalOpen(true);
  };

  // Função para fechar modal de ONUs
  const handleCloseOnusModal = () => {
    setOnusModalOpen(false);
    setSelectedPortForOnus(null);
  };

  const handleOpenCtosModal = (port: Port) => {
    setSelectedPortForCtos(port);
    setCtosModalOpen(true);
  };

  const handleCloseCtosModal = () => {
    setCtosModalOpen(false);
    setSelectedPortForCtos(null);
  };

  // Função para remover porta
  const handleRemovePort = (port: Port) => {
    setRemovePortId(port.id);
  };

  const handleConfirmRemovePort = async () => {
    if (!selectedCard || removePortId === null) return;

    setRemoveLoading(true);
    try {
      const response = await api.delete(`/olts/cards/${selectedCard.id}/ports/${removePortId}`);

      if (response.data.success) {
        toast.success('Porta removida com sucesso');
        setPorts(prev => prev.filter(p => p.id !== removePortId));
        setRemovePortId(null);
      } else {
        toast.error(response.data?.message || 'Erro ao remover porta');
      }
    } catch (err: any) {
      console.error('Erro ao remover porta:', err);
      toast.error(err.response?.data?.message || 'Erro ao remover porta');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleCancelRemovePort = () => {
    setRemovePortId(null);
  };

  // Função para fechar modal
  const handleClose = () => {
    setPorts([]);
    setPortToEdit(null);
    onClose();
  };

  // Buscar portas quando o modal abrir
  useEffect(() => {
    if (open && selectedCard && oltId) {
      fetchPorts(selectedCard.slot);
    }
  }, [open, selectedCard, oltId]);

  /** Ordena portas pelo número da porta (numérico: 1, 2, 3, ..., 10, 11) */
  const sortPortsByNumber = (list: Port[]): Port[] =>
    [...list].sort((a, b) => (parseInt(a.portNumber, 10) || 0) - (parseInt(b.portNumber, 10) || 0));

  const getPortStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPortStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'inactive':
        return 'Inativa';
      case 'maintenance':
        return 'Manutenção';
      default:
        return 'Desconhecida';
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ p: 1.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PortIcon color="primary" />
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                Portas do Card - Slot {selectedCard?.slot}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setPortToEdit(null);
                  setPortFormOpen(true);
                }}
                disabled={portsLoading}
                size="small"
                sx={{ fontSize: '0.8rem' }}
              >
                Cadastrar
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={syncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                onClick={syncPorts}
                disabled={syncLoading || portsLoading}
                size="small"
                sx={{ fontSize: '0.8rem' }}
              >
                {syncLoading ? 'Sincronizando...' : 'Sincronizar Portas'}
              </Button>
              <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5 }}>
          {portsLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{
              '& .MuiTableCell-root': {
                py: 0.5,
                px: 1.5,
                fontSize: '0.875rem',
                lineHeight: 1.2
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                py: 0.75,
                fontWeight: 600,
                fontSize: '0.8rem'
              }
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Slot</strong></TableCell>
                    <TableCell><strong>Porta</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>ONUs Ativas</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>CTOs</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Máximo ONUs</strong></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Potência TX</strong></TableCell>
                    <TableCell><strong>Descrição</strong></TableCell>
                    <TableCell><strong>Ações</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ports.length > 0 ? (
                    ports.map((port) => (
                      <TableRow key={port.id} hover>
                        <TableCell>
                          <Chip
                            label={`Slot ${port.slot}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`Porta ${port.portNumber}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPortStatusLabel(port.status)}
                            color={getPortStatusColor(port.status) as any}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${port.onuCount} ONUs`}
                            color="info"
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenOnusModal(port)}
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 }, fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Chip
                              label={`${port.ctoCount ?? 0} CTOs`}
                              color="secondary"
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenCtosModal(port)}
                              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 }, fontSize: '0.7rem', height: 20 }}
                            />
                            {port.ctoAlert === 'healthy' && (
                              <CheckCircleIcon
                                sx={{ color: '#4CAF50', fontSize: 18, flexShrink: 0 }}
                                titleAccess="Pelo menos uma CTO online e nenhuma offline"
                              />
                            )}
                            {port.ctoAlert === 'partial_offline' && (
                              <ErrorOutlineIcon
                                sx={{ color: '#FFC107', fontSize: 18, flexShrink: 0 }}
                                titleAccess="Pelo menos uma CTO offline"
                              />
                            )}
                            {port.ctoAlert === 'all_offline' && (
                              <CircleIcon
                                sx={{
                                  color: '#F44336',
                                  fontSize: 12,
                                  flexShrink: 0,
                                  animation: 'ctoOfflineBlink 0.65s ease-in-out infinite alternate',
                                  '@keyframes ctoOfflineBlink': {
                                    from: { opacity: 1 },
                                    to: { opacity: 0.25 }
                                  }
                                }}
                                titleAccess="Todas as CTOs offline"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {port.maxOnus} ONUs
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {port.txpower !== undefined ? `${port.txpower} dBm` : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {port.description || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.25} alignItems="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleStartEdit(port)}
                              sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              title="Editar"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemovePort(port)}
                              sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                              title="Remover porta"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                          <PortIcon color="action" sx={{ fontSize: 48 }} />
                          <Typography variant="body1" color="text.secondary">
                            Nenhuma porta encontrada
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Clique em &quot;Cadastrar&quot; para adicionar uma porta manualmente ou &quot;Sincronizar Portas&quot; para importar da OLT
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={handleClose} color="primary" size="small" sx={{ fontSize: '0.8rem' }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação para remover porta */}
      <Dialog
        open={removePortId !== null}
        onClose={handleCancelRemovePort}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remover porta</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover esta porta do sistema? As ONUs vinculadas também serão removidas. Esta ação não desprovisiona a ONU na OLT.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemovePort} disabled={removeLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmRemovePort}
            color="error"
            variant="contained"
            disabled={removeLoading}
            startIcon={removeLoading ? <CircularProgress size={18} /> : <DeleteIcon />}
          >
            {removeLoading ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Cadastro/Edição de Porta */}
      <OlTCardPortForm
        open={portFormOpen}
        onClose={() => {
          setPortFormOpen(false);
          setPortToEdit(null);
        }}
        onSuccess={() => selectedCard && fetchPorts(selectedCard.slot)}
        oltId={oltId || ''}
        cardSlot={selectedCard?.slot || 0}
        initialData={portToEdit}
      />

      {/* Modal de ONUs */}
      <OnusModal
        open={onusModalOpen}
        onClose={handleCloseOnusModal}
        oltId={oltId}
        cardSlot={selectedCard?.slot}
        portNumber={selectedPortForOnus?.portNumber}
        portId={selectedPortForOnus?.id}
        cardId={selectedCard?.id}
      />

      {/* Modal de CTOs */}
      <CtosModal
        open={ctosModalOpen}
        onClose={handleCloseCtosModal}
        cardSlot={selectedCard?.slot}
        portNumber={selectedPortForCtos?.portNumber}
        portId={selectedPortForCtos?.id}
      />
    </>
  );
};

export default PortsModal; 